import type { MountableApp } from '@opengov/psp-contract';

/**
 * Default time budget for a remote module's dynamic import to settle.
 * If `import()` neither resolves nor rejects within this window, `loadModule()`
 * rejects with `ModuleLoadTimeoutError` so the shell can show recoverable UI
 * instead of an infinite spinner.
 */
export const DEFAULT_MODULE_LOAD_TIMEOUT_MS = 10_000;

/**
 * Default time budget for the post-import HEAD probe used to classify HTTP
 * failures. Much shorter than `DEFAULT_MODULE_LOAD_TIMEOUT_MS` because by the
 * time we run the probe the user has already waited up to ~10s for the failed
 * import — adding anything longer would re-introduce the slow-spinner regression
 * `ModuleLoadTimeoutError` exists to prevent.
 */
export const DEFAULT_PROBE_TIMEOUT_MS = 3_000;

/**
 * Total number of `loadModuleOnce()` invocations the retry wrapper will make
 * before surfacing the last error to the caller. PSS-2536 settles this at one
 * initial load attempt followed by three retries — the literal reading of the
 * Jira AC ("retries 3 times with 1s/2s/4s backoff").
 */
export const DEFAULT_MODULE_LOAD_MAX_ATTEMPTS = 4;

/**
 * Backoff schedule (in milliseconds) applied between consecutive load
 * attempts. Length must equal `DEFAULT_MODULE_LOAD_MAX_ATTEMPTS - 1` because
 * there is no point sleeping after the final attempt has thrown. Index `i`
 * is the wait between attempt `i + 1` and attempt `i + 2`, so this delivers
 * the canonical 1s/2s/4s exponential progression from PSS-2536 §P1-10.
 */
export const DEFAULT_MODULE_LOAD_BACKOFF_MS = [1000, 2000, 4000] as const;

/**
 * Query-param key used to cache-bust a module URL on retry (PSS-2668). The
 * browser caches both successful AND failed dynamic `import()` results by URL
 * in its module map, so re-importing the same URL after a failure replays the
 * cached rejection without ever hitting the network — which is why the
 * PSS-2536 retry loop alone could not recover a tab open across a redeploy.
 * Appending a unique nonce makes each retry a distinct module-map key, forcing
 * a real fetch that also bypasses the HTTP cache and pulls the freshly-deployed
 * entry.
 */
export const RELOAD_NONCE_PARAM = '__pspReload';

/**
 * Returns `url` with the cache-bust nonce param set to `token`. Built on the
 * URL API (not string concatenation) so a URL that already carries a query
 * string keeps its existing params (`?foo=bar` → `?foo=bar&__pspReload=…`) and
 * a prior nonce is overwritten rather than accumulated.
 *
 * `token` MUST be distinct per retry (callers use `${attempt}-${Date.now()}`):
 * an identical token re-keys the same cached rejection and the retry becomes a
 * no-op.
 *
 * Note: only the entry URL is nonced. Relative chunk specifiers inside the
 * loaded module (`import('./chunk-HASH.js')`) resolve against the entry's base
 * URL with the query component dropped per the URL spec, so chunks still load
 * by their real hashed names — the nonce never leaks onto chunk requests.
 */
export function withReloadNonce(url: string, token: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set(RELOAD_NONCE_PARAM, token);
    return parsed.href;
  } catch {
    // `new URL(url)` throws on a relative specifier (no base). PSP resolvers
    // return absolute URLs, so this is a defensive fallback only — append the
    // nonce by hand. Correctness still holds: the nonce just needs to make the
    // string distinct enough to re-key the browser module map on retry.
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${RELOAD_NONCE_PARAM}=${token}`;
  }
}

/**
 * Thrown when a remote module's dynamic import does not settle within `timeoutMs`.
 * Identity is preserved by `loadModule()` (it does not re-wrap this class), so
 * callers can distinguish timeouts from generic load failures via `instanceof`
 * or `error.name === 'ModuleLoadTimeoutError'`.
 *
 * Note: `Promise.race` cannot cancel the underlying browser import; the request
 * may still complete in the background, but the shell stops waiting on it.
 */
export class ModuleLoadTimeoutError extends Error {
  readonly name = 'ModuleLoadTimeoutError';

  constructor(
    readonly url: string,
    readonly timeoutMs: number
  ) {
    super(`Timed out loading module from ${url} after ${timeoutMs}ms`);
  }
}

/**
 * Thrown when a post-import HEAD probe confirms that the entry-chunk URL
 * returned a non-2xx HTTP status (other than 405, which we treat as
 * "HEAD not supported by this CDN" and fall through). Carries `status` and
 * `statusText` so observability dashboards can filter by HTTP code, and an
 * optional `cause` preserving the original `import()` rejection stack.
 *
 * Identity is preserved by `loadModule()` (it does not re-wrap this class).
 *
 * Note: `cause` is declared as a class field rather than passed to
 * `super(message, { cause })` because PSP's tsconfig is `lib: ["ES2020"]` and
 * the 2-arg `Error` constructor signature lives in `lib.es2022.error.d.ts`.
 * The class-field approach is ES2020-compatible.
 */
export class ModuleHttpError extends Error {
  readonly name = 'ModuleHttpError';
  readonly cause?: unknown;

  constructor(
    readonly url: string,
    readonly status: number,
    readonly statusText: string,
    options?: { cause?: unknown }
  ) {
    super(`Module entry chunk at ${url} returned HTTP ${status} ${statusText}`);
    if (options?.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

/**
 * Options for `loadModule()`. All fields are non-user-facing seams used by
 * tests and (for `retry.onAttempt` + `signal`) by `useModuleLoader`. Production
 * call sites only need to provide `signal` + `retry.onAttempt`, except sign-out
 * loading a module that isn't mounted (`mountedModulesRegistry`), which passes
 * `timeoutMs`, `probeTimeoutMs`, and `retry.maxAttempts: 1` to stay inside its
 * budget.
 *
 * @internal
 */
export interface LoadModuleOptions {
  /** Override the default import timeout (ms). */
  timeoutMs?: number;
  /**
   * Override the dynamic import. Tests use this to feed synthetic module
   * shapes (validation) or never-settling promises (timeout). When omitted,
   * the real `import(/* @vite-ignore *\/ url)` is used.
   */
  importer?: (url: string) => Promise<unknown>;
  /**
   * Override the HEAD probe `fetch`. Tests use this to inject synthetic
   * `Response`s, simulate probe failures, or simulate uncooperative fetchers
   * that ignore the AbortSignal. When omitted, the real `globalThis.fetch` is
   * used.
   */
  fetcher?: (url: string, init?: RequestInit) => Promise<Response>;
  /** Override the default probe timeout (ms). */
  probeTimeoutMs?: number;
  /**
   * Retry policy. Defaults to the production policy (4 total attempts,
   * 1s/2s/4s backoff between attempts). Pass `{ maxAttempts: 1 }` from tests
   * that intentionally assert single-attempt semantics under fake timers.
   */
  retry?: {
    maxAttempts?: number;
    /** Length must equal `maxAttempts - 1`. Validated at runtime. */
    backoffMs?: readonly number[];
    /**
     * Called with the 1-indexed total attempt number BEFORE each attempt
     * fires (including the first). `useModuleLoader` uses this to surface
     * "Retrying… (attempt N of M)" progress in the UI without having to
     * understand the retry policy itself.
     */
    onAttempt?: (attempt: number) => void;
  };
  /**
   * Aborts pending backoff sleeps and skips subsequent attempts. On abort,
   * `loadModule()` rejects with `DOMException('Module load aborted', 'AbortError')`.
   * Production callers (`useModuleLoader`) pass an `AbortController.signal`
   * that's aborted on hook cleanup or URL change.
   *
   * Caveat: the underlying dynamic `import()` is not cancelable. If a signal
   * aborts while `loadModuleOnce()` is mid-flight, the in-flight import keeps
   * running in the background; the retry loop only checks the signal at
   * iteration boundaries and during backoff sleeps. The consumer (hook) is
   * expected to drop the stale resolution via its own guard.
   */
  signal?: AbortSignal;
}

/**
 * Returns `true` when `error` is likely transient and worth retrying.
 *
 * Retryable:
 * - `ModuleLoadTimeoutError` — slow CDN / hung connection; another attempt may complete.
 * - `ModuleHttpError` with `status >= 500` — server-side transient (503, 504, …).
 * - `ModuleHttpError` with `status === 408` — Request Timeout.
 * - `ModuleHttpError` with `status === 429` — Too Many Requests; backoff is the
 *   correct response.
 * - `ModuleHttpError` with `status === 404` — Not Found. Although a 404 is
 *   "deterministic" for a given URL, `loadModule()` appends a unique
 *   `__pspReload` nonce to each retry URL (PSS-2668). That makes every retry
 *   a distinct URL the browser has never cached, forcing a real HTTP request
 *   that bypasses any disk-cached 404 from the first attempt. This recovers
 *   the common stale-deploy scenario (CDN served a 404 for a briefly-missing
 *   entry during a rolling deploy; subsequent requests succeed) without a
 *   hard-reload. If the file is genuinely absent, all 4 nonced retries 404
 *   too, `attemptsExhausted` fires the one-per-session auto-reload guard in
 *   `useModuleLoader`, and the user lands on the error page.
 * - Generic wrapped errors (`Error('Failed to load module from <url>: …')`) — we
 *   can't introspect the underlying cause through the re-wrap, but most fall
 *   into the transient-network bucket (CORS preflight, ECONNREFUSED, browser-side
 *   fetch abort). The cost of a futile retry on a permanent network problem is
 *   bounded by the backoff budget (~7s under the default schedule) and we
 *   judge that worth paying for the transient case.
 *
 * Not retryable:
 * - `ModuleHttpError` with `status` in 4xx (other than 404/408/429) — auth and
 *   permissions errors (401, 403, …) are deterministic; retrying will not change
 *   the response.
 * - Shape-validation errors thrown after a successful import (`Module must have
 *   a default export`, `Module must export a mount() function`, `Module must
 *   export an unmount() function`) — the module body will not reshape itself.
 *   The check is a literal substring match against the wrapped error message
 *   produced by `loadModuleOnce()`; the constants below must stay in sync
 *   with the validation throws inside `loadModuleOnce`.
 * - `AbortError` from the caller's signal — caller-initiated cancellation,
 *   never retried.
 */
export function isRetryableLoadError(error: unknown): boolean {
  if (error instanceof ModuleLoadTimeoutError) return true;
  if (error instanceof DOMException && error.name === 'AbortError') return false;

  if (error instanceof ModuleHttpError) {
    const status = error.status;
    if (status >= 500) return true;
    if (status === 408) return true;
    if (status === 429) return true;
    if (status === 404) return true;
    return false;
  }

  if (error instanceof Error) {
    const msg = error.message;
    if (msg.includes('default export')) return false;
    if (msg.includes('mount() function')) return false;
    if (msg.includes('unmount() function')) return false;
    return true;
  }

  return true;
}

/**
 * Sleep for `ms` milliseconds, but reject early if `signal` aborts. On abort
 * the returned promise rejects with `DOMException('Module load aborted',
 * 'AbortError')`, allowing the retry loop to propagate the cancellation
 * without an additional `signal.aborted` check at every call site. Cleans up
 * both the timer and the abort listener in `finally` so neither leaks across
 * outcomes.
 */
async function sleepWithAbort(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) {
    throw new DOMException('Module load aborted', 'AbortError');
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  let abortListener: (() => void) | undefined;

  try {
    await new Promise<void>((resolve, reject) => {
      timer = setTimeout(resolve, ms);
      if (signal) {
        abortListener = () => {
          reject(new DOMException('Module load aborted', 'AbortError'));
        };
        signal.addEventListener('abort', abortListener);
      }
    });
  } finally {
    clearTimeout(timer);
    if (signal && abortListener) {
      signal.removeEventListener('abort', abortListener);
    }
  }
}

/**
 * Single-attempt loader. Carries the existing PSS-2534 timeout race and
 * PSS-2535 HTTP probe semantics; not exported because retry policy is the
 * job of the wrapping `loadModule()`.
 */
async function loadModuleOnce(url: string, options: LoadModuleOptions = {}): Promise<MountableApp> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_MODULE_LOAD_TIMEOUT_MS;
  const probeTimeoutMs = options.probeTimeoutMs ?? DEFAULT_PROBE_TIMEOUT_MS;
  const importer =
    options.importer ?? ((moduleUrl: string) => import(/* @vite-ignore */ moduleUrl));
  const fetcher = options.fetcher ?? ((u: string, init?: RequestInit) => fetch(u, init));

  console.log(`[loadModule] Fetching module: ${url}`);

  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      reject(new ModuleLoadTimeoutError(url, timeoutMs));
    }, timeoutMs);
  });

  let module: unknown;
  try {
    module = await Promise.race([importer(url), timeoutPromise]);
  } catch (error) {
    clearTimeout(timeoutHandle);
    if (error instanceof ModuleLoadTimeoutError) {
      console.error(`[loadModule] ✗ Timed out loading module: ${url}`);
      throw error;
    }

    // Best-effort HTTP classification: did the failure look HTTP-shaped?
    // A short, bounded HEAD probe tells us. If the probe itself fails, times
    // out, returns 2xx, returns 405 (HEAD not supported), or otherwise can't
    // reliably classify, we preserve the original import error via the generic
    // wrap below rather than fabricate a status.
    //
    // The probe uses Promise.race + AbortController as a belt-and-suspenders
    // timeout: the race bounds wall-clock time even when a custom or buggy
    // `fetcher` ignores the AbortSignal; the abort cleanly cancels cooperative
    // fetchers and surfaces an AbortError on the awaited promise.
    const probeAbort = new AbortController();
    let probeTimer: ReturnType<typeof setTimeout> | undefined;
    const probeTimeoutPromise = new Promise<never>((_, reject) => {
      probeTimer = setTimeout(() => {
        probeAbort.abort();
        reject(new DOMException('Probe timeout', 'AbortError'));
      }, probeTimeoutMs);
    });
    try {
      const response = await Promise.race([
        fetcher(url, {
          method: 'HEAD',
          mode: 'cors',
          credentials: 'same-origin',
          signal: probeAbort.signal,
        }),
        probeTimeoutPromise,
      ]);
      if (response.status !== 405 && !response.ok) {
        const httpError = new ModuleHttpError(url, response.status, response.statusText, {
          cause: error,
        });
        console.error(`[loadModule] ✗ HTTP ${response.status} for module: ${url}`);
        throw httpError;
      }
    } catch (probeError) {
      if (probeError instanceof ModuleHttpError) throw probeError;
      // Probe failed (AbortError from probe timeout — either via race or via
      // cooperative abort — network down, CORS denied, etc). Fall through to
      // the generic re-wrap below.
    } finally {
      clearTimeout(probeTimer);
    }

    console.error(`[loadModule] ✗ Failed to load module: ${url}`, error);
    throw new Error(
      `Failed to load module from ${url}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
  clearTimeout(timeoutHandle);

  try {
    const typedModule = module as { default?: { mount?: unknown; unmount?: unknown } };

    if (!typedModule.default) {
      throw new Error('Module must have a default export');
    }

    if (typeof typedModule.default.mount !== 'function') {
      throw new Error('Module must export a mount() function');
    }

    if (typeof typedModule.default.unmount !== 'function') {
      throw new Error('Module must export an unmount() function');
    }

    console.log(`[loadModule] ✓ Module loaded successfully: ${url}`);
    return typedModule.default as MountableApp;
  } catch (error) {
    console.error(`[loadModule] ✗ Failed to load module: ${url}`, error);
    throw new Error(
      `Failed to load module from ${url}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Session-scoped cache of successfully loaded modules, keyed by the bare
 * module URL (retry nonces are never used as keys). Lets a later
 * `useModuleLoader` call for the same URL mount without a loading spinner.
 */
const resolvedModuleCache = new Map<string, MountableApp>();

/** In-flight `loadModule` promises so concurrent callers share one fetch. */
const inflightModuleLoads = new Map<string, Promise<MountableApp>>();

/**
 * Returns a successfully loaded module for `url` if one was already resolved
 * this session, otherwise `null` (including while a load is still in flight).
 */
export function getCachedModule(url: string): MountableApp | null {
  return resolvedModuleCache.get(url) ?? null;
}

/** Clears the session module cache. Exported for tests. */
export function clearModuleCache(): void {
  resolvedModuleCache.clear();
  inflightModuleLoads.clear();
}

/**
 * Loads a remote ES module from the given URL, retrying transient failures
 * with exponential backoff. Default policy: 4 total attempts (1 initial load
 * + 3 retries) with 1s/2s/4s backoff between attempts. Tests opt out via
 * `options.retry = { maxAttempts: 1 }`.
 *
 * Successful loads are cached for the JS session by bare URL so a second
 * `loadModule(sameUrl)` (or `getCachedModule`) returns the same instance
 * without re-fetching. Failed loads are not cached.
 *
 * Rejects with:
 * - `ModuleLoadTimeoutError` — final attempt's dynamic import did not settle
 *   within `timeoutMs`.
 * - `ModuleHttpError` — final attempt's import rejected AND the HEAD probe
 *   confirmed a non-2xx status (other than 405). The retry wrapper does not
 *   re-wrap this class; identity is preserved.
 * - A plain `Error('Failed to load module from <url>: …')` — final attempt's
 *   import rejected for a non-HTTP reason (network down, CORS denied, syntax
 *   error in the module body, etc).
 * - `DOMException('Module load aborted', 'AbortError')` — the consumer's
 *   signal aborted between attempts or during the backoff sleep. Callers
 *   should treat this as cancellation, not as a failed load.
 *
 * @param url - The URL of the remote ES module to load
 * @param options - Internal test seams + production-only retry/signal options
 * @returns Promise that resolves to the loaded module
 */
export async function loadModule(
  url: string,
  options: LoadModuleOptions = {}
): Promise<MountableApp> {
  const cached = resolvedModuleCache.get(url);
  if (cached) {
    return cached;
  }

  const inflight = inflightModuleLoads.get(url);
  if (inflight) {
    return inflight;
  }

  const loadPromise = loadModuleUncached(url, options).then(
    (module) => {
      resolvedModuleCache.set(url, module);
      inflightModuleLoads.delete(url);
      return module;
    },
    (error: unknown) => {
      inflightModuleLoads.delete(url);
      throw error;
    }
  );

  inflightModuleLoads.set(url, loadPromise);
  return loadPromise;
}

async function loadModuleUncached(
  url: string,
  options: LoadModuleOptions = {}
): Promise<MountableApp> {
  const maxAttempts = options.retry?.maxAttempts ?? DEFAULT_MODULE_LOAD_MAX_ATTEMPTS;
  // When the caller overrides only `maxAttempts`, slice the default schedule
  // to match so a single-attempt opt-out doesn't have to specify an empty
  // `backoffMs: []`. The slice is safe because the default schedule is at
  // least as long as any reasonable override.
  const backoffMs =
    options.retry?.backoffMs ?? DEFAULT_MODULE_LOAD_BACKOFF_MS.slice(0, maxAttempts - 1);

  // Catch the off-by-one early: backoff has one fewer entry than attempts
  // because the final attempt has nothing after it.
  if (backoffMs.length !== maxAttempts - 1) {
    throw new Error(
      `loadModule: backoffMs.length (${backoffMs.length}) must equal maxAttempts - 1 (${
        maxAttempts - 1
      })`
    );
  }

  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (options.signal?.aborted) {
      throw new DOMException('Module load aborted', 'AbortError');
    }
    options.retry?.onAttempt?.(attempt);

    // PSS-2668: attempt 1 imports the bare URL so the happy path benefits from
    // the browser HTTP cache (no perf regression, no behavior change for
    // CloudFront/S3-backed modules). Retries import a uniquely-nonced URL so
    // they re-key the browser module map — escaping the cached rejection that
    // makes same-URL retries futile — and re-fetch the freshly-deployed entry.
    // The `${attempt}-` prefix keeps nonces distinct even when `Date.now()` is
    // frozen (e.g. fake timers in tests).
    const attemptUrl = attempt === 1 ? url : withReloadNonce(url, `${attempt}-${Date.now()}`);

    try {
      return await loadModuleOnce(attemptUrl, options);
    } catch (err) {
      lastError = err;
      const isLastAttempt = attempt === maxAttempts;
      const isRetryable = isRetryableLoadError(err);

      if (isLastAttempt || !isRetryable) {
        throw err;
      }

      const delay = backoffMs[attempt - 1];
      console.warn(
        `[loadModule] Attempt ${attempt}/${maxAttempts} failed for ${url}, retrying in ${delay}ms`,
        err
      );

      await sleepWithAbort(delay, options.signal);
    }
  }

  // Unreachable: the for-loop either returns or throws on every path. The
  // explicit throw keeps TypeScript happy without a `never` return type.
  throw lastError ?? new Error('loadModule: unreachable');
}
